import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Settings, Cookie, Shield, BarChart3, X } from 'lucide-react';
import { useCookieConsent } from '@/context/CookieConsentContext';

interface CookieConsentProps {
  className?: string;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ className = '' }) => {
  const { 
    showBanner, 
    preferences, 
    updatePreferences, 
    acceptAll, 
    rejectAll,
    savePreferences 
  } = useCookieConsent();
  
  const [showDetails, setShowDetails] = useState(false);
  const [tempPreferences, setTempPreferences] = useState(preferences);

  useEffect(() => {
    setTempPreferences(preferences);
  }, [preferences]);

  if (!showBanner) return null;

  const handleTogglePreference = (key: keyof typeof preferences) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setTempPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSavePreferences = () => {
    updatePreferences(tempPreferences);
    savePreferences();
  };

  const cookieTypes = [
    {
      key: 'necessary' as const,
      icon: <Shield className="w-4 h-4" />,
      title: 'Necessary Cookies',
      description: 'Essential for the website to function properly. These cannot be disabled.',
      required: true
    },
    {
      key: 'analytics' as const,
      icon: <BarChart3 className="w-4 h-4" />,
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      required: false
    },
    {
      key: 'marketing' as const,
      icon: <Cookie className="w-4 h-4" />,
      title: 'Marketing Cookies',
      description: 'Used to track visitors across websites to display relevant and engaging advertisements.',
      required: false
    }
  ];

  return (
    <div className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm ${className}`}>
      <div className="fixed bottom-4 left-4 right-4 max-w-2xl mx-auto">
        <Card className="border-2 border-[#6C4F77]/20 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6">
            {!showDetails ? (
              // Simple Banner View
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Cookie className="w-6 h-6 text-[#6C4F77] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#6C4F77] text-lg mb-2">
                      We value your privacy
                    </h3>
                    <p className="text-[#2C2C2C] text-sm leading-relaxed">
                      We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                      You can manage your preferences or learn more in our{' '}
                      <a 
                        href="/privacy-policy" 
                        className="text-[#6C4F77] underline hover:text-[#F4978E] transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    onClick={acceptAll}
                    className="bg-[#6C4F77] hover:bg-[#5A3A68] text-white flex-1"
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={() => setShowDetails(true)}
                    variant="outline"
                    className="border-[#6C4F77] text-[#6C4F77] hover:bg-[#F7F5F2] flex-1"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Preferences
                  </Button>
                  <Button
                    onClick={rejectAll}
                    variant="ghost"
                    className="text-[#6C4F77] hover:bg-[#F7F5F2] flex-1"
                  >
                    Reject All
                  </Button>
                </div>
              </div>
            ) : (
              // Detailed Preferences View
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#6C4F77] text-lg flex items-center gap-2">
                    <Cookie className="w-5 h-5" />
                    Cookie Preferences
                  </h3>
                  <Button
                    onClick={() => setShowDetails(false)}
                    variant="ghost"
                    size="sm"
                    className="text-[#6C4F77] hover:bg-[#F7F5F2]"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <p className="text-[#2C2C2C] text-sm">
                  Choose which cookies you want to accept. You can change these settings at any time.
                </p>

                <Separator />

                <div className="space-y-4 max-h-60 overflow-y-auto">
                  {cookieTypes.map((cookieType) => (
                    <div key={cookieType.key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="text-[#6C4F77]">
                            {cookieType.icon}
                          </div>
                          <h4 className="font-medium text-[#6C4F77]">
                            {cookieType.title}
                          </h4>
                          {cookieType.required && (
                            <span className="text-xs bg-[#6C4F77]/10 text-[#6C4F77] px-2 py-1 rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                        <Switch
                          checked={tempPreferences[cookieType.key]}
                          onCheckedChange={() => handleTogglePreference(cookieType.key)}
                          disabled={cookieType.required}
                          className="data-[state=checked]:bg-[#6C4F77]"
                        />
                      </div>
                      <p className="text-[#2C2C2C] text-xs pl-6">
                        {cookieType.description}
                      </p>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="bg-[#6C4F77] hover:bg-[#5A3A68] text-white flex-1"
                  >
                    Save Preferences
                  </Button>
                  <Button
                    onClick={acceptAll}
                    variant="outline"
                    className="border-[#6C4F77] text-[#6C4F77] hover:bg-[#F7F5F2] flex-1"
                  >
                    Accept All
                  </Button>
                </div>

                <p className="text-xs text-[#6C4F77]/70 text-center">
                  For more information, read our{' '}
                  <a 
                    href="/privacy-policy" 
                    className="underline hover:text-[#F4978E] transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a 
                    href="/terms-of-service" 
                    className="underline hover:text-[#F4978E] transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookieConsent;